import { useMemo, useState } from 'react'
import { BarChart3, TrendingUp, AlertCircle, RefreshCw } from 'lucide-react'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { FreshnessRibbon } from '@/components/FreshnessRibbon'
import { useKpiSummary, useKpiStats } from '@/hooks/useKpi'
import { cn } from '@/lib/utils'

const COLORS = {
  primary: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  gray: '#6b7280',
}

const STATUS_COLORS: Record<string, string> = {
  pending: COLORS.warning,
  approved: COLORS.success,
  rejected: COLORS.danger,
  expired: COLORS.gray,
  executed: COLORS.primary,
  failed: COLORS.danger,
  draft: COLORS.gray,
}

function formatPercentage(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return `${date.getMonth() + 1}/${date.getDate()}`
}

interface KpiCardProps {
  title: string
  value: string
  target: string
  status: 'good' | 'warning' | 'danger'
  icon: React.ReactNode
}

function KpiCard({ title, value, target, status, icon }: KpiCardProps) {
  const bgColor = {
    good: 'bg-green-50 border-green-200',
    warning: 'bg-yellow-50 border-yellow-200',
    danger: 'bg-red-50 border-red-200',
  }[status]

  const textColor = {
    good: 'text-green-700',
    warning: 'text-yellow-700',
    danger: 'text-red-700',
  }[status]

  return (
    <div className={cn('rounded-lg border-2 p-4 shadow-sm', bgColor)}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className={cn('mt-1 text-3xl font-bold', textColor)}>{value}</p>
          <p className="mt-1 text-xs text-muted-foreground">目標: {target}</p>
        </div>
        <div className={cn('rounded-full p-3', textColor)}>{icon}</div>
      </div>
    </div>
  )
}

export function KpiDashboardPage() {
  const [period, setPeriod] = useState<'week' | 'month'>('week')

  const { data: summaryData, isLoading: summaryLoading, error: summaryError } = useKpiSummary()
  const { data: statsData, isLoading: statsLoading, error: statsError } = useKpiStats(period)

  const summary = summaryData?.summary
  const stats = statsData?.stats

  // KPI Status Calculation
  const adoptionStatus = useMemo(() => {
    if (!summary) return 'warning'
    if (summary.adoptionRate >= 0.6) return 'good'
    if (summary.adoptionRate >= 0.4) return 'warning'
    return 'danger'
  }, [summary])

  const ignoreStatus = useMemo(() => {
    if (!summary) return 'warning'
    if (summary.ignoreRate <= 0.3) return 'good'
    if (summary.ignoreRate <= 0.5) return 'warning'
    return 'danger'
  }, [summary])

  const resubmitStatus = useMemo(() => {
    if (!summary) return 'warning'
    if (summary.resubmitRate >= 0.5) return 'good'
    if (summary.resubmitRate >= 0.3) return 'warning'
    return 'danger'
  }, [summary])

  // Chart Data
  const trendChartData = useMemo(() => {
    return summary?.trends.map((t) => ({
      date: formatDate(t.date),
      採択率: (t.adoptionRate * 100).toFixed(1),
      無視率: (t.ignoreRate * 100).toFixed(1),
      再提案成功率: (t.resubmitRate * 100).toFixed(1),
    }))
  }, [summary])

  const statusPieData = useMemo(() => {
    return stats?.byStatus.map((s) => ({
      name: s.status,
      value: s.count,
    }))
  }, [stats])

  const templateBarData = useMemo(() => {
    return stats?.byTemplate.map((t) => ({
      name: t.templateName || t.templateId.slice(0, 8),
      採択率: (t.adoptionRate * 100).toFixed(1),
      total: t.total,
    }))
  }, [stats])

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold">KPI ダッシュボード</h1>
        <p className="text-muted-foreground">
          提案エンジンのパフォーマンスを可視化します（MVP成功指標）
        </p>
      </div>

      <FreshnessRibbon kinds={['funnel', 'ingest']} />

      {(summaryLoading || statsLoading) && (
        <div className="text-sm text-muted-foreground">読み込み中...</div>
      )}

      {(summaryError || statsError) && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="h-5 w-5" />
          KPI データの取得に失敗しました
        </div>
      )}

      {summary && (
        <>
          {/* KPI Summary Cards */}
          <div className="grid gap-4 md:grid-cols-3">
            <KpiCard
              title="提案採択率"
              value={formatPercentage(summary.adoptionRate)}
              target="60%以上"
              status={adoptionStatus}
              icon={<TrendingUp className="h-6 w-6" />}
            />
            <KpiCard
              title="提案無視率"
              value={formatPercentage(summary.ignoreRate)}
              target="30%以下"
              status={ignoreStatus}
              icon={<AlertCircle className="h-6 w-6" />}
            />
            <KpiCard
              title="再提案成功率"
              value={formatPercentage(summary.resubmitRate)}
              target="50%以上"
              status={resubmitStatus}
              icon={<RefreshCw className="h-6 w-6" />}
            />
          </div>

          {/* Trend Chart */}
          <div className="rounded-lg border bg-card p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                <h2 className="font-semibold">週次トレンド（7日移動平均）</h2>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="採択率" stroke={COLORS.success} strokeWidth={2} />
                <Line type="monotone" dataKey="無視率" stroke={COLORS.danger} strokeWidth={2} />
                <Line
                  type="monotone"
                  dataKey="再提案成功率"
                  stroke={COLORS.primary}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {stats && (
        <>
          {/* Period Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">集計期間:</span>
            <button
              onClick={() => setPeriod('week')}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                period === 'week'
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-muted bg-background text-muted-foreground hover:bg-muted'
              )}
            >
              週次
            </button>
            <button
              onClick={() => setPeriod('month')}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                period === 'month'
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-muted bg-background text-muted-foreground hover:bg-muted'
              )}
            >
              月次
            </button>
          </div>

          {/* Status Distribution */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border bg-card p-6 shadow-sm">
              <h3 className="mb-4 font-semibold">ステータス別分布</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={statusPieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusPieData?.map((entry) => (
                      <Cell
                        key={`cell-${entry.name}`}
                        fill={STATUS_COLORS[entry.name] || COLORS.gray}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Template Adoption Rate */}
            <div className="rounded-lg border bg-card p-6 shadow-sm">
              <h3 className="mb-4 font-semibold">テンプレート別採択率</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={templateBarData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="採択率" fill={COLORS.primary} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Date-based Stats */}
          <div className="rounded-lg border bg-card p-6 shadow-sm">
            <h3 className="mb-4 font-semibold">日別提案数推移</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.byDate}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={formatDate} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="approved" name="承認" stackId="a" fill={COLORS.success} />
                <Bar dataKey="rejected" name="却下" stackId="a" fill={COLORS.danger} />
                <Bar dataKey="expired" name="期限切れ" stackId="a" fill={COLORS.gray} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  )
}
